<div class="content-section mx-auto space-y-6">
  <article class="">
    <div
      class="reveal flex flex-col md:flex-row py-6 items-center justify-start md:items-stretch gap-6"
    >
      <!-- Gallery -->
      @defer (when activeImages().length > 0) {
        <app-product-details-gallery [images]="activeImages()" />
      } @placeholder {
        <div class="w-full md:w-1/2 animate-pulse aspect-square"></div>
      } @loading {
        <div class="w-full md:w-1/2 animate-pulse aspect-square"></div>
      }
      <section class="grow flex flex-col gap-y-3 w-full md:w-fit">
        <h3 class="font-semibold text-xl">
          {{ product()?.name || ('Loading...' | translate) }}
        </h3>
        <!-- Ratings -->
        <section>
          <div class="flex items-center gap-x-2.5">
            <span
              class="flex text-yellow-400 **:stroke-current **:size-2 sm:**:size-4 sm:gap-x-1.5"
            >
              @if (product()?.reviewsAverage) {
                <!-- simple repeat for rating logic can be applied, or just static stars assuming an angular component will render it -->
                <sham-i-RatingStar />
                <sham-i-RatingStar />
                <sham-i-RatingStar />
                <sham-i-RatingStar />
                <sham-i-RatingStar class="*:fill-current/5" />
              } @else {
                @for (star of [1, 2, 3, 4, 5]; track star) {
                  <sham-i-star />
                }
              }
            </span>
            <span class="text-sm text-second-text-color font-bold tabular-nums">
              {{ product()?.reviewsCount || 0 }} {{ 'Reviews' | translate }}
            </span>
          </div>
        </section>
        <!-- Stock Status -->
        <dl class="flex gap-x-1 font-bold text-sm">
          <dt class="">{{ 'Availability' | translate }}:</dt>
          @if (activeStockStatus()) {
            <dd class="text-blue">
              {{ 'In Stock' | translate }} ({{ maxQuantity() }})
            </dd>
          } @else {
            <dd class="text-red line-through">{{ 'Out of Stock' | translate }}</dd>
          }
        </dl>
        <dl class="flex gap-x-1 font-semibold text-sm">
          <dt>{{ 'Sold By' | translate }}:</dt>
          <dd>
            <button
              type="button"
              (click)="goToSellerProfile(product()?.seller?.sellerId)"
              class="text-blue hover:underline focus-visible:underline"
            >
              {{ product()?.seller?.sellerName }}
            </button>
          </dd>
        </dl>
        <!-- Pricing -->
        <div class="flex items-end-safe gap-x-2 md:gap-x-3 tabular-nums">
          <span
            class="text-blue font-semibold text-xs sm:text-base md:text-4xl flex items-baseline gap-x-px sm:gap-x-2"
          >
            <span> SYP </span>
            <span class="tabular-nums"> {{ activePriceAmount() }} </span>
          </span>
          @if (activeSalesInfo(); as SALES) {
            <span
              class="line-through text-base sm:text-lg md:text-2xl text-white-dark-a"
              >SYP {{ SALES.amount }}</span
            >
            <span
              class="bg-[#FEF2EF] text-[#F77B61] text-4xs sm:text-3xs md:text-xs p-px sm:p-1 rounded border border-[#F77B61]"
            >
              {{ SALES.percentage }}% {{ 'Off' | translate }}
            </span>
          }
        </div>
        <!-- Attributes -->
        <section class="space-y-3 mt-3">
          @for (group of attributes(); track group.nameId) {
            <div class="space-y-1">
              <h4 class="text-lg">{{ group.name }}</h4>
              @if (isColorGroup(group.name)) {
                <menu
                  class="flex gap-2 w-full overflow-x-auto p-1"
                  role="radiogroup"
                  [attr.aria-label]="group.name"
                >
                  @for (val of group.values; track val.valueId) {
                    @let selected = getSelectedValue(group.nameId) === val.valueId;
                    @let color = getSwatchColor(val.value);

                    <li>
                      @if (color) {
                        <button
                          type="button"
                          (click)="selectAttribute(group.nameId, val.valueId)"
                          class="size-6 sm:size-7 rounded-full border transition-all"
                          [style.backgroundColor]="color"
                          [class.border-blue]="selected"
                          [class.ring-2]="selected"
                          [attr.title]="val.value"
                        ></button>
                      } @else {
                        <button
                          type="button"
                          (click)="selectAttribute(group.nameId, val.valueId)"
                          class="btn btn-sm min-w-12"
                          [class.btn-outline]="selected"
                        >
                          {{ val.value }}
                        </button>
                      }
                    </li>
                  }
                </menu>
              } @else {
                <menu class="flex gap-x-3 w-full overflow-x-auto p-1">
                  @for (val of group.values; track val.valueId) {
                    <li>
                      <button
                        type="button"
                        (click)="selectAttribute(group.nameId, val.valueId)"
                        class="btn btn-sm min-w-12 text-nowrap transition-all duration-200"
                        [class.btn-active]="
                          getSelectedValue(group.nameId) !== val.valueId
                        "
                        [class.btn-outline]="
                          getSelectedValue(group.nameId) === val.valueId
                        "
                        [class.border-blue]="
                          getSelectedValue(group.nameId) === val.valueId
                        "
                        [class.hover:bg-transparent]="
                          getSelectedValue(group.nameId) === val.valueId
                        "
                        [attr.aria-pressed]="
                          getSelectedValue(group.nameId) === val.valueId
                        "
                      >
                        {{ val.value }}
                      </button>
                    </li>
                  }
                </menu>
              }
            </div>
          }
        </section>
        <!-- Quantity -->
        <div class="w-full max-w-55 space-y-3">
          <label for="product-quantity" class="block m:text-lg leading-none">
            {{ 'Quantity' | translate }}
          </label>
          <div
            class="join h-12 w-full rounded-lg border border-base-300 overflow-hidden"
          >
            <button
              type="button"
              (click)="decrementQuantity()"
              [disabled]="!activeStockStatus() || quantity() <= MIN_QUANTITY"
              class="btn join-item h-full w-1/4 bg-transparent! text-3xl font-normal border-0"
              [attr.aria-label]="'Decrease quantity' | translate"
            >
              &minus;
            </button>
            <input
              id="product-quantity"
              type="number"
              inputmode="numeric"
              [ngModel]="quantity()"
              (ngModelChange)="onQuantityChange($event)"
              [min]="activeStockStatus() ? MIN_QUANTITY : 0"
              [max]="maxQuantity()"
              [disabled]="!activeStockStatus()"
              class="input join-item h-full min-w-0 grow bg-transparent! text-center text-3xl font-normal border-0 focus:outline-none"
              [attr.aria-label]="'Product quantity' | translate"
            />
            <button
              type="button"
              (click)="incrementQuantity()"
              [disabled]="!activeStockStatus() || quantity() >= maxQuantity()"
              class="btn join-item h-full w-1/4 bg-transparent! text-3xl font-normal border-0"
              [attr.aria-label]="'Increase quantity' | translate"
            >
              &plus;
            </button>
          </div>
        </div>
        <!-- Feature -->
        <div class="mt-6 space-y-1">
          <h4 class="font-bold mb-4">{{ 'Features' | translate }}</h4>
          <ul class="grid grid-cols-2 gap-2 text-sm!">
            <li class="flex gap-x-2">
              <span class="text-blue">
                <sham-i-Medal />
              </span>
              <span>{{ 'Warranty Available' | translate }}</span>
            </li>
            <li class="flex gap-x-2">
              <span class="text-blue">
                <sham-i-Truck />
              </span>
              <span>{{ 'Free Delivery' | translate }}</span>
            </li>
            <li class="flex gap-x-2">
              <span class="text-blue">
                <sham-i-Handshake />
              </span>
              <span>{{ '100% Money Back Guarantee' | translate }}</span>
            </li>
            <li class="flex gap-x-2">
              <span class="text-blue">
                <sham-i-Headphones />
              </span>
              <span>{{ '24/7 Customer Support' | translate }}</span>
            </li>
            <li class="flex gap-x-2">
              <span class="text-blue">
                <sham-i-CreditCard />
              </span>
              <span>{{ 'Secure Payment' | translate }}</span>
            </li>
          </ul>
        </div>
        <!-- Actions -->
        <menu class="flex mt-auto gap-x-3 sm:gap-x-4 md:mb-4">
          <button
            type="button"
            (click)="buyNow()"
            [disabled]="!canPurchase()"
            class="btn btn-sm sm:btn-md btn-primary grow"
          >
            {{ 'Buy Now' | translate }}
          </button>
          <button
            type="button"
            (click)="addToCart()"
            [disabled]="!canPurchase()"
            class="btn btn-sm sm:btn-md btn-primary btn-outline"
          >
            {{ 'Add to Cart' | translate }}
          </button>
          <button
            type="button"
            (click)="addToWishlist()"
            [attr.aria-label]="'Add to favorites' | translate"
            class="group/action relative btn btn-sm sm:btn-md btn-circle btn-outline text-black/50 hover:text-white-light hover:bg-blue flex flex-col justify-center items-center gap-0 border border-[#E8E8E8]"
          >
            <sham-i-favourite01 class="*:size-6 mt-0.5" />
            <span
              role="tooltip"
              [attr.dir]="languageService.currentLang() === 'ar' ? 'rtl' : 'ltr'"
              class="pointer-events-none absolute bottom-full start-1/2 mb-2 ltr:-translate-x-1/2 rtl:translate-x-1/2 whitespace-nowrap rounded bg-blue px-3 py-1.5 text-xs text-white-light opacity-0 transition-opacity after:absolute after:top-full after:start-1/2 after:ltr:-translate-x-1/2 after:rtl:translate-x-1/2 after:border-4 after:border-transparent after:border-t-blue group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
            >
              {{ 'Add to favorites' | translate }}
            </span>
            <!-- <span class="text-[6px]">{{ 'Favorite' | translate }}</span> -->
          </button>
          <button
            type="button"
            (click)="copyProductLink()"
            [attr.aria-label]="'Share product link' | translate"
            class="group/action relative btn btn-sm sm:btn-md btn-circle btn-outline text-black/50 hover:text-white-light hover:bg-blue flex flex-col justify-center items-center gap-0 border border-[#E8E8E8]"
          >
            <sham-i-share-08 class="*:size-5 mr-px" />
            <span
              role="tooltip"
              [attr.dir]="languageService.currentLang() === 'ar' ? 'rtl' : 'ltr'"
              class="pointer-events-none absolute bottom-full start-1/2 mb-2 ltr:-translate-x-1/2 rtl:translate-x-1/2 whitespace-nowrap rounded bg-blue px-3 py-1.5 text-xs text-white-light opacity-0 transition-opacity after:absolute after:top-full after:start-1/2 after:ltr:-translate-x-1/2 after:rtl:translate-x-1/2 after:border-4 after:border-transparent after:border-t-blue group-hover/action:opacity-100 group-focus-visible/action:opacity-100"
            >
              {{ 'Share product link' | translate }}
            </span>
            <!-- <span class="text-[6px]">{{ 'Share' | translate }}</span> -->
          </button>
        </menu>
      </section>
    </div>
  </article>
  <!-- Tabs -->
  <section class="reveal border rounded">
    <div
      class="tabs tabs-border [&_input:before]:border-blue [&>div]:rounded-t-none [&>div]:border-x-0 [&>div]:p-4"
    >
      <input
        type="radio"
        name="product_info_tabs"
        class="tab"
        [attr.aria-label]="'About this item' | translate"
        checked="checked"
      />
      <div class="tab-content border-base-300 bg-base-100">
        <div
          class="flex flex-col md:items-start md:flex-row gap-4 min-h-0 [&_dd]:text-white-dark-a [&_p]:text-white-dark-a"
        >
          <section class="md:pe-2 md:flex-1/2 xl:flex-3/7 space-y-3 text-sm">
            <h4 class="font-bold text-base">{{ 'About This Item' | translate }}</h4>
            <p>
              {{ product()?.shortDescription }}
            </p>
            <p>
              {{ product()?.longDescription }}
            </p>
          </section>
          <hr class="border self-stretch h-auto" />
          <section class="md:ps-2 md:flex-1/2 xl:flex-4/7 space-y-3 text-sm">
            <h4 class="font-bold text-base">
              {{ 'Shipping Information' | translate }}
            </h4>
            <dl>
              <div class="flex flex-col sm:flex-row gap-1">
                <dt class="min-w-1/4 font-semibold">
                  {{ 'Local Shipping' | translate }}:
                </dt>
                <dd class="grow">
                  {{ matchedVariant()?.processingTimeDays || 1 }}
                  {{ 'Days' | translate }}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      </div>

      <input
        type="radio"
        name="product_info_tabs"
        class="tab"
        [attr.aria-label]="'Specification' | translate"
      />
      <div class="tab-content border-base-300 bg-base-100">
        <div
          class="flex flex-col md:items-start md:flex-row gap-4 min-h-0 [&_dd]:text-white-dark-a [&_p]:text-white-dark-a"
        >
          <section class="md:ps-2 md:flex-1/2 space-y-3 text-sm">
            <h4 class="font-bold text-base text-blue">
              {{ 'Product Details' | translate }}
            </h4>
            <dl class="space-y-4">
              @if (matchedVariant(); as variant) {
                <div class="flex flex-col sm:flex-row gap-y-1 gap-x-2">
                  <dt class="min-w-1/4 font-semibold">{{ 'Weight' | translate }}:</dt>
                  <dd>{{ variant.weightKg }} {{ 'kg' | translate }}</dd>
                </div>
                <div class="flex flex-col sm:flex-row gap-y-1 gap-x-2">
                  <dt class="min-w-1/4 font-semibold">
                    {{ 'Dimensions' | translate }}:
                  </dt>
                  <dd>
                    {{ variant.lengthCm }} x {{ variant.widthCm }} x
                    {{ variant.heightCm }} {{ 'cm' | translate }}
                  </dd>
                </div>
              }
            </dl>
          </section>
          <hr class="border self-stretch h-auto" />
          <section class="md:ps-2 md:flex-1/2 space-y-3 text-sm">
            <h4 class="font-bold text-base">{{ 'Specification' | translate }}</h4>
            @if (product()?.specifications?.length) {
              <dl class="space-y-4">
                @if (product()?.specifications; as specs) {
                  @for (item of specs; track item.title) {
                    <div class="flex flex-col sm:flex-row gap-1">
                      <dt class="min-w-1/4 font-semibold">{{ item.title }}:</dt>
                      <dd class="grow">{{ item.value }}</dd>
                    </div>
                  }
                }
              </dl>
            } @else {
              <div>{{ 'N/A' | translate }}</div>
            }
          </section>
        </div>
      </div>

      <input
        type="radio"
        name="product_info_tabs"
        class="tab"
        [attr.aria-label]="'Reviews' | translate"
      />
      <div class="tab-content border-base-300 bg-base-100">
        @if (product()?.id) {
          <app-product-reviews
            [productId]="product()!.id"
            [productSlug]="product()!.slug"
            [reviewsAverage]="product()!.reviewsAverage"
            [reviewsCount]="product()!.reviewsCount"
          />
        }
      </div>
    </div>
  </section>
  @if (hasFrequentProducts()) {
    <section class="reveal space-y-4">
      <h3 class="font-bold">{{ 'Frequently Bought Together' | translate }}</h3>
      @defer {
        <app-frequent-products
          [slug]="slug()"
          (hasProductsChange)="setHasFrequentProducts($event)"
          (totalChange)="setFrequentProductsTotal($event)"
        />
      }
      <div class="text-center my-6">
        <button class="btn btn-primary">
          {{ 'Buy The Whole Package for' | translate }} SYP
          {{ frequentProductsTotal() | number: '1.0-2' }}
        </button>
      </div>
    </section>
  }
  @if (hasSameSellerProducts()) {
    <section class="reveal space-y-4">
      <h3 class="font-bold">{{ 'From The Same Seller' | translate }}</h3>
      @defer {
        <app-same-seller-products
          [slug]="slug()"
          (hasProductsChange)="setHasSameSellerProducts($event)"
        />
      }
    </section>
  }
</div>
